// What the connection card keeps while the user is off the page for IFTTT
// sign-in or a service's OAuth, so it can carry on the connect flow when they
// come back. It is written only as the user leaves, read once on return, and
// swept once stale, so viewing Applet pages leaves nothing in localStorage.
import { CONNECTION_STATE_KEY_PREFIX } from "./utils"

export const RESUME_STATE_TTL_MS = 60 * 60 * 1000

// showSuccessConnection also carries a flag per service module_name.
type AnimationState = { collection: Record<string, { played: boolean; [moduleName: string]: boolean }> }

export type ResumeState = {
  authRequestedAt?: string
  // Service module_name → whether it was connected when the user left.
  servicesGranted?: Record<string, boolean>
  animation?: AnimationState
}

type StoredResumeState = ResumeState & { savedAt: number }

const isFresh = (stored: unknown, now: number): stored is StoredResumeState => {
  const savedAt = (stored as StoredResumeState | null)?.savedAt
  return typeof savedAt === "number" && now - savedAt < RESUME_STATE_TTL_MS
}

const parse = (raw: string | null): unknown => {
  try {
    return raw === null ? null : JSON.parse(raw)
  } catch {
    return null
  }
}

export function saveResumeState(storageId: string, state: ResumeState) {
  try {
    window.localStorage.setItem(storageId, JSON.stringify({ ...state, savedAt: Date.now() }))
  } catch {
    // Storage full or blocked: the user resumes by clicking connect again.
  }
}

// Removes the record as it reads it, so a reload after resuming does not resume twice.
export function takeResumeState(storageId: string): ResumeState | undefined {
  try {
    const stored = parse(window.localStorage.getItem(storageId))
    window.localStorage.removeItem(storageId)
    if (!isFresh(stored, Date.now())) return undefined

    const { authRequestedAt, servicesGranted, animation } = stored
    return { authRequestedAt, servicesGranted, animation }
  } catch {
    return undefined
  }
}

// Removes every connection card record past its TTL, and every record from
// before resume state existed: those held the whole Redux store (hundreds of
// KB each) and were never removed unless the user finished enabling the Applet.
export function sweepStaleResumeStates() {
  try {
    const storage = window.localStorage
    const now = Date.now()
    const staleKeys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(
      (key): key is string =>
        key !== null && key.startsWith(CONNECTION_STATE_KEY_PREFIX) && !isFresh(parse(storage.getItem(key)), now),
    )
    staleKeys.forEach(key => storage.removeItem(key))
  } catch {
    // Storage blocked: there is nothing to sweep.
  }
}
